import { IUser } from 'src/app/features/settings/models/settings.models';
import { IOrderUser } from 'src/app/features/orders/models/order.model';

type AvatarUser = IUser | IOrderUser | { name: string; first_name?: string; last_name?: string };

function hashString(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
}

/**
 * The identity an avatar's colour is derived from.
 *
 * Keyed on the SAME value the initials come from, so an avatar's colour and its
 * letters can never disagree about who they represent: if two avatars show the
 * same initials for the same person, they show the same colour.
 *
 * This is deliberately not id- or email-first. The same person arrives from
 * different endpoints in different shapes — `/me` carries a full user record,
 * while a notification's `users` list may be a trimmed object with a different
 * numeric id and no email at all. Keying on either gave one human two different
 * colours on the same screen (green in the sidebar footer, dark red in the
 * notification recipients table). The display name is the one field present and
 * identical in every payload.
 *
 * Trade-off: two different people who share a display name also share a colour.
 * That is preferable to one person rendering in two colours, and the name is
 * always shown next to the avatar anyway.
 *
 * Email and id remain fallbacks for objects with no usable name.
 */
function stableKey(user: AvatarUser): string {
  const fullname = getUserFullname(user).trim().toLowerCase();
  if (fullname && fullname !== 'unknown') return fullname;

  if ('email' in user && user.email) return user.email.trim().toLowerCase();
  if ('id' in user && user.id != null) return String(user.id);
  return fullname;
}

export function getUserFullname(user: AvatarUser): string {
  if ('first_name' in user && user.first_name && 'last_name' in user && user.last_name) {
    return `${user.first_name} ${user.last_name}`;
  }
  return user.name || 'Unknown';
}

/** Always returns two uppercase initials for consistency across avatars. */
export function getUserInitials(user: AvatarUser): string {
  if ('first_name' in user && user.first_name && 'last_name' in user && user.last_name) {
    return (user.first_name.charAt(0) + user.last_name.charAt(0)).toUpperCase();
  }
  const name = getUserFullname(user).trim();
  if (!name) return 'U';
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * Normalises a stored avatar into something usable as an `<img src>`.
 * The API may return a CDN URL, a data URI, or raw base64 (sometimes an SVG).
 * Returns '' when there is no image, so callers can fall back to initials.
 */
export function getUserAvatarSrc(image: string | null | undefined): string {
  if (!image) return '';
  if (image.startsWith('data:') || image.startsWith('http')) return image;
  try {
    const decoded = atob(image);
    if (decoded.trim().startsWith('<?xml')) return `data:image/svg+xml;base64,${image}`;
  } catch {
    // Not valid base64 — fall through to the permissive data URI below.
  }
  return `data:image/*;base64,${image}`;
}

const AVATAR_PALETTE = [
  // Reds
  '#821500', // Brick Red
  '#6d1020', // Crimson
  '#7a2030', // Ruby
  // Ambers & Browns
  '#765700', // Amber
  '#6b3a1f', // Sienna
  '#5c4400', // Dark Mustard
  // Greens
  '#006f43', // Success Green
  '#1f5c35', // Forest Green
  '#2d5a27', // Olive Green
  '#005c4a', // Emerald
  // Teals
  '#004768', // Info Navy
  '#005f73', // Deep Teal
  '#0a4d6e', // Ocean
  // Blues & Navies
  '#0c1431', // Oxford Blue
  '#003a7d', // Royal Blue
  '#1a3a5c', // Steel Navy
  '#0d2b45', // Dark Cerulean
  // Purples
  '#6449db', // Volta Purple
  '#4f38b0', // Indigo
  '#3d2d8a', // Royal Purple
  '#2a1e5e', // Deep Purple
  // Slates
  '#1c2b36', // Slate
  '#2e3f4f', // Charcoal
  '#3b2f5e', // Purple Slate
];

export function getUserColor(user: AvatarUser): string {
  const hash = hashString(stableKey(user));
  return AVATAR_PALETTE[hash % AVATAR_PALETTE.length];
}

// ─── Mailboxes ───────────────────────────────────────────────────────────────

/** Minimal shape a mailbox tag needs. Matches Smart Inbox's `IAccountMailbox`. */
export type TaggedMailbox = { id?: number | null; name?: string | null; email?: string | null };

/** Background / border / text triple for a mailbox tag. */
export interface MailboxTagColor {
  bg: string;
  border: string;
  text: string;
}

/**
 * Pastel tints for mailbox tags, in the same idiom as `$classification-palette`
 * in `styles/ui/_colors.scss` — soft in-brand background, one step darker
 * border, dark text of the same hue.
 *
 * Kept in TypeScript rather than SCSS because the assignment of a mailbox to a
 * tint is computed at runtime (see `assignMailboxTagColors`), so the value has
 * to be bound inline. Ten hues is well past the number of mailboxes anyone
 * connects, which is what lets the assignment stay collision-free.
 */
const MAILBOX_TAG_PALETTE: MailboxTagColor[] = [
  { bg: '#e8e4fb', border: '#cec6f5', text: '#3d2a9e' }, // Lavender (brand)
  { bg: '#d5f2ff', border: '#a5e2ff', text: '#00506f' }, // Sky
  { bg: '#d8f0e5', border: '#b3e0cd', text: '#006f43' }, // Mint
  { bg: '#fbe1f1', border: '#f3c6e4', text: '#8a2b6d' }, // Blush
  { bg: '#dcecd9', border: '#bedcb8', text: '#1f5c2e' }, // Sage
  { bg: '#fff2d0', border: '#ffeab0', text: '#765700' }, // Sand
  { bg: '#fbddd8', border: '#ffbaaf', text: '#821500' }, // Clay
  { bg: '#e6e7ef', border: '#d2d5e4', text: '#3f4462' }, // Slate
  { bg: '#d6f0f2', border: '#aadfe3', text: '#005f73' }, // Teal
  { bg: '#dfe6fb', border: '#c3d0f5', text: '#1a3a5c' }, // Periwinkle
];

/**
 * The mailbox's own preferred slot in the palette, keyed on its **id**.
 *
 * Unlike `getUserColor`, this deliberately does NOT key on the display name.
 * A mailbox arrives in one shape from one endpoint and always carries its id,
 * and the name is user-editable: renaming an account patches `mailbox.name`
 * in place, which would silently reshuffle the colour of every thread already
 * on screen. The id is stable and unique, so the colour is too.
 *
 * Name/email remain fallbacks for the rare payload with no id.
 */
function preferredMailboxSlot(mailbox: TaggedMailbox): number {
  const key =
    mailbox.id != null
      ? `mailbox:${mailbox.id}`
      : (mailbox.email || mailbox.name || '').trim().toLowerCase();
  if (!key) return 0;
  return hashString(key) % MAILBOX_TAG_PALETTE.length;
}

/**
 * Tint for a single mailbox, with no knowledge of the others. Used for mailboxes
 * outside the connected list — a thread reached through the shared inbox belongs
 * to someone else's mailbox, which never appears in your own accounts.
 */
export function getMailboxColor(mailbox: TaggedMailbox): MailboxTagColor {
  return MAILBOX_TAG_PALETTE[preferredMailboxSlot(mailbox)];
}

/**
 * Assigns a tint to every mailbox in a list such that no two share one.
 *
 * Each mailbox asks for its own hashed slot first, so a mailbox keeps its colour
 * as the list changes around it; only when two want the same slot does the
 * second probe forward to the next free one. Assignment walks the list in id
 * order so it never depends on the order the API returned.
 *
 * Beyond `MAILBOX_TAG_PALETTE.length` mailboxes duplicates become unavoidable
 * and the extras fall back to their preferred slot.
 */
export function assignMailboxTagColors(mailboxes: TaggedMailbox[]): Record<number, MailboxTagColor> {
  const size = MAILBOX_TAG_PALETTE.length;
  const ordered = mailboxes
    .filter((mailbox): mailbox is TaggedMailbox & { id: number } => mailbox.id != null)
    .sort((a, b) => a.id - b.id);

  const taken = new Set<number>();
  const colors: Record<number, MailboxTagColor> = {};

  for (const mailbox of ordered) {
    const preferred = preferredMailboxSlot(mailbox);
    let slot = preferred;
    for (let probe = 1; probe <= size && taken.has(slot); probe++) {
      slot = (preferred + probe) % size;
    }
    taken.add(slot);
    colors[mailbox.id] = MAILBOX_TAG_PALETTE[slot];
  }

  return colors;
}

/**
 * Three-letter uppercase code identifying a mailbox, taken from the first three
 * letters of its name — or of its email's local part when it has no name.
 *
 * Up to three characters, not always three: a short name yields a short code
 * ("Jo" → `JO`, "A" → `A`). Even width is a presentation concern and belongs to
 * the tag's `min-width`, not here. Punctuation and spaces are dropped first
 * ("J. Dupont" → `JDU`, not `J. `). Returns '' when there is nothing usable, so
 * callers can skip rendering entirely.
 */
export function getMailboxTagLabel(mailbox: TaggedMailbox): string {
  const source = (mailbox.name || '').trim() || (mailbox.email || '').split('@')[0].trim();
  const letters = source.replace(/[^\p{L}\p{N}]/gu, '');
  return letters.slice(0, 3).toUpperCase();
}
